import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Tag, Headphones, Award, CheckCircle, FileText, Globe, Plane, Hotel, Phone, MapPin, Building2, Sparkles, Users, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import GoogleReviews from '../components/GoogleReviews';
import InquiryForm from '../components/InquiryForm';
import SEO from '../components/SEO';
import { whyChooseUs, services } from '../mock';
import { visaPagesData, destinationPagesData } from '../data/detailPages';

const Home = () => {
  const iconMap = { Shield, Zap, Tag, HeadphonesIcon: Headphones, Award, CheckCircle, FileText, Globe, Plane, Hotel, Users, Briefcase, Sparkles, Building2 };
  const popularVisas = Object.values(visaPagesData);
  const destinations = Object.values(destinationPagesData);

  return (
    <div className="min-h-screen">
      <SEO
        title="Visa Consultant in Surat | Tour Packages, Flight & Hotel Booking"
        description="Varni Visa - Premium visa consultancy in Surat. Dubai, Schengen, Thailand, Singapore visas. Kashmir, Bali, Vietnam tour packages. Flight & hotel bookings. Call +91 90169 30091."
        keywords="Visa Consultant in Surat, Dubai Visa Services, Schengen Visa Consultant, Thailand Visa Services, International Tour Packages, Kashmir Tour Packages, Bali Tour Packages, Flight Booking, Hotel Booking, Thailand DMC, Pattaya Tour Packages, Thailand Ground Handling, Thailand B2B Tour Operator, Varni2Fly Thailand"
        path="/"
      />

      {/* Hero */}
      <section
        className="relative min-h-[640px] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.7)), url('https://images.pexels.com/photos/7079773/pexels-photo-7079773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3 text-white">
              <span className="inline-block bg-[#F7945D]/20 border border-[#F7945D]/40 text-[#F7945D] text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                Surat&apos;s Trusted Visa & Travel Agency
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-5 animate-fade-in">
                Global Visa & <span className="text-[#F7945D]">Travel Solutions</span>
              </h1>
              <p className="text-lg md:text-xl text-white/85 mb-8 max-w-xl">
                Visa Assistance · International Tours · Flight Booking · Hotel Reservations. Plan your dream journey with experts who care.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/contact" data-testid="hero-consult">
                  <Button size="lg" className="bg-[#F7945D] hover:bg-[#e67f3d] text-white">
                    Get Free Consultation <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <a href="https://wa.me/919016930091" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp">
                  <Button size="lg" className="bg-[#25D366] hover:bg-[#1fb855]">WhatsApp Now</Button>
                </a>
                <a href="tel:+919016930091">
                  <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#2D8DB4]">
                    <Phone size={16} className="mr-2" /> +91 90169 30091
                  </Button>
                </a>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-md">
                {[{ n: '5000+', l: 'Happy Travelers' }, { n: '50+', l: 'Countries' }, { n: '98%', l: 'Visa Success' }].map((s, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur border border-white/15 rounded-lg p-3 text-center">
                    <div className="text-2xl font-extrabold text-[#F7945D]">{s.n}</div>
                    <div className="text-[11px] uppercase tracking-wider text-white/75">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Inquiry Form */}
            <div className="lg:col-span-2">
              <InquiryForm
                title="Quick Inquiry"
                subtitle="Get a callback in 30 minutes"
                showTypeSelector
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">Our Services</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Complete Travel Solutions Under One Roof</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">From visa applications to ground handling in Thailand — we take care of every detail of your journey</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || CheckCircle;
              return (
                <Link key={service.id} to={service.link} className="group">
                  <Card className="h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-[#F7945D]">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Icon className="text-white" size={26} />
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-gray-900">{service.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                      <span className="text-[#2D8DB4] font-semibold text-sm inline-flex items-center group-hover:text-[#F7945D]">
                        Learn More <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Visa Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">Popular Visa Services</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Visa Consultancy You Can Trust</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Fast processing, expert documentation, and high approval rates for global destinations</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularVisas.map((v) => (
              <Link key={v.slug} to={`/visa/${v.slug}`} data-testid={`home-visa-${v.slug}`} className="group">
                <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div
                    className="h-44 bg-cover bg-center relative"
                    style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(45,141,180,0.7)), url('${v.heroImage}')` }}
                  >
                    <div className="absolute top-3 left-3 text-3xl">{v.flag}</div>
                    <div className="absolute bottom-3 left-4 text-white">
                      <h3 className="text-2xl font-bold">{v.country} Visa</h3>
                      <p className="text-xs text-white/90">{v.entryType}</p>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Processing</div>
                        <div className="font-semibold text-gray-900">{v.processingTime}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">From</div>
                        <div className="font-bold text-[#F7945D] text-lg">{v.price}</div>
                      </div>
                    </div>
                    <span className="text-[#2D8DB4] font-semibold text-sm inline-flex items-center group-hover:text-[#F7945D]">
                      View Details <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Tour Packages */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">Popular Tour Packages</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Handpicked Destinations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">From Himalayan retreats to tropical getaways — curated for unforgettable journeys</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {destinations.map((d) => (
              <Link key={d.slug} to={`/destinations/${d.slug}`} data-testid={`home-dest-${d.slug}`} className="group">
                <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="h-56 overflow-hidden relative">
                    <img src={d.heroImage} alt={d.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 text-white">
                      <h3 className="text-2xl font-bold">{d.name}</h3>
                      <p className="text-xs text-white/90 flex items-center gap-1"><MapPin size={11} /> {d.duration}</p>
                    </div>
                    <div className="absolute top-3 right-3 bg-white/95 text-[#F7945D] font-bold text-sm px-3 py-1 rounded-full">
                      From {d.price}
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{d.overview}</p>
                    <span className="text-[#2D8DB4] font-semibold text-sm inline-flex items-center group-hover:text-[#F7945D]">
                      View Itinerary <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-br from-[#2D8DB4]/5 to-[#F7945D]/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">Why Choose Us</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">The Varni Visa Advantage</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">A trusted travel partner committed to making your journey seamless</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Globe, title: 'Direct Thailand Operations', desc: 'Powered by Varni2Fly — our Thailand DMC division in Pattaya' },
              { icon: MapPin, title: 'Local Pattaya Support', desc: '24/7 ground team for hotel, transfer, sightseeing issues' },
              { icon: Tag, title: 'Better Pricing', desc: 'Direct contracts mean wholesale rates passed on to you' },
              { icon: Building2, title: 'Reliable Ground Handling', desc: 'End-to-end logistics for groups of 2 to 500+ pax' },
              { icon: Sparkles, title: 'Customized Thailand Packages', desc: 'Tailored itineraries — leisure, honeymoon, group, corporate' },
              { icon: Shield, title: 'Expert Visa Guidance', desc: '98% visa approval rate across Dubai, Schengen, UK, Canada' },
              { icon: Headphones, title: 'Dedicated Support', desc: 'Indian + Thai team available 7 days a week' },
              { icon: Award, title: 'Trusted Travel Partner', desc: '5000+ travellers served across Gujarat and India' },
              { icon: CheckCircle, title: 'Complete Solutions', desc: 'Visa, tours, flights, hotels, transfers — all under one roof' }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="text-white" size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1 text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Varni2Fly Thailand DMC Section */}
      <section className="py-16 bg-white relative overflow-hidden" data-testid="varni2fly-section">
        <div className="absolute -right-20 top-10 w-72 h-72 bg-[#F7945D]/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute -left-20 bottom-10 w-72 h-72 bg-[#2D8DB4]/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-[#F7945D]/15 border border-[#F7945D]/30 text-[#F7945D] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                <Sparkles size={12} /> Thailand DMC Partner
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-3">
                Varni<span className="text-[#F7945D]">2</span>Fly Thailand
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                Our <strong>Thailand destination management division</strong> with direct local operations in <strong>Pattaya</strong>, providing ground handling across Pattaya, Bangkok, Phuket and Krabi.
              </p>
              <p className="text-gray-600 mb-6">
                From hotel reservations and airport transfers to island tours, group travel and corporate events — Varni2Fly delivers vertically-integrated Thailand operations, which means better pricing and more reliable service for you.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { icon: Hotel, label: 'Hotels' },
                  { icon: Plane, label: 'Transfers' },
                  { icon: MapPin, label: 'Tours' },
                  { icon: Building2, label: 'Ground Ops' }
                ].map((it, i) => {
                  const I = it.icon;
                  return (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                      <I size={20} className="mx-auto mb-1 text-[#2D8DB4]" />
                      <div className="text-xs font-semibold text-gray-700">{it.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/thailand-dmc">
                  <Button size="lg" className="bg-[#2D8DB4] hover:bg-[#247a9a]">
                    Explore Varni2Fly DMC <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/destinations/thailand">
                  <Button size="lg" variant="outline">View Thailand Packages</Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img src="https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxUaGFpbGFuZCUyMGJlYWNofGVufDB8fHx8MTc4MDIzNzQ2MHww&ixlib=rb-4.1.0&q=85" alt="Bangkok" loading="lazy" className="rounded-xl h-48 w-full object-cover shadow-lg" />
                <img src="https://images.pexels.com/photos/19595559/pexels-photo-19595559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Phuket" loading="lazy" className="rounded-xl h-32 w-full object-cover shadow-lg" />
              </div>
              <div className="space-y-4 pt-8">
                <img src="https://images.pexels.com/photos/35154841/pexels-photo-35154841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Krabi" loading="lazy" className="rounded-xl h-32 w-full object-cover shadow-lg" />
                <img src="https://images.pexels.com/photos/19595559/pexels-photo-19595559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Pattaya" loading="lazy" className="rounded-xl h-48 w-full object-cover shadow-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence Trust Bar */}
      <section className="py-12 bg-gray-900 text-white" data-testid="presence-bar">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-xs mb-2">Our Global Presence</p>
            <h2 className="text-2xl md:text-3xl font-bold">India HQ + Direct Thailand Operations</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 flex gap-4 items-start">
              <div className="w-12 h-12 bg-[#2D8DB4] rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#F7945D] font-semibold mb-1">India Office</div>
                <h3 className="font-bold mb-1">Surat, Gujarat</h3>
                <p className="text-sm text-white/75">105 Anupam Business Hub, Yogi Chowk, Surat, Gujarat, India</p>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 flex gap-4 items-start">
              <div className="w-12 h-12 bg-[#F7945D] rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#F7945D] font-semibold mb-1">Thailand Operations</div>
                <h3 className="font-bold mb-1">Pattaya, Thailand</h3>
                <p className="text-sm text-white/75">Direct destination management through <strong>Varni2Fly</strong> — local team, ground handling, 24/7 support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-95">Get a free consultation today. Talk to our experts about your travel and visa needs.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#2D8DB4] hover:bg-gray-100">Contact Us</Button>
            </Link>
            <a href="tel:+919016930091">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2D8DB4]">
                <Phone size={18} className="mr-2" /> +91 90169 30091
              </Button>
            </a>
            <a href="tel:+917388813555">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2D8DB4]">
                <Phone size={18} className="mr-2" /> +91 73888 13555
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
